import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Table } from 'rsuite';
import { TableProps } from 'rsuite/lib/Table';
import { CompetitorPriceCell, HoverNomeColuna, MargemCell, PMZCell, PrecoSugeridoCell, PrecoVigenteCell } from '../../..';
import { GerenciadorPrecos } from '../../../../../../../@types/GerenciadorPrecos';
import { ButtonPrimary, Layout } from '../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../components/AlertWithTimestamp';
import { StoreProductClusterCell } from '../../../../../../../components/TableCells/StoreProductClusterCell';
import { Exception } from '../../../../../../../data';
import { SET_GET_CLUSTER_STORE_MODAL_IS_OPEN } from '../../../../../../../reducers/gerenciadorPrecos';
import { RowData } from '../../../../../../../reducers/gerenciadorPrecos/datapoints';
import {
  RESET_GET_CLUSTER_STORE_MODAL_DATAPOINTS,
  selectorGetClusterStoreModalDatapoints,
  SET_GET_CLUSTER_STORE_MODAL_DATAPOINTS,
  UPDATE_GET_CLUSTER_STORE_MODAL_DATAPOINT,
} from '../../../../../../../reducers/getClusterStoreModal/datapoints';
import {
  CLEAR_EDITED_CLUSTER_STORE_DATAPOINTS,
  selectorGetEditedClusterStoreDatapoints,
  UPDATE_EDITED_CLUSTER_STORE_DATAPOINT,
} from '../../../../../../../reducers/getClusterStoreModal/editedClusterStoreDatapoints';
import {
  RESET_GET_CLUSTER_STORE_MODAL_PAGINATION,
  selectorGetClusterStoreModalPagination,
  SET_GET_CLUSTER_STORE_MODAL_ACTIVE_PAGE,
  SET_GET_CLUSTER_STORE_MODAL_DISPLAY_LENGTH,
} from '../../../../../../../reducers/getClusterStoreModal/pagination';
import {
  RESET_GET_CLUSTER_STORE_MODAL_SORT,
  selectorGetClusterStoreModalSort,
  SET_GET_CLUSTER_STORE_MODAL_SORT,
} from '../../../../../../../reducers/getClusterStoreModal/sort';
import {
  RESET_GET_CLUSTER_STORE_MODAL_TOTAL_ELEMENTS,
  selectorGetClusterStoreModalTotalElements,
  SET_GET_CLUSTER_STORE_MODAL_TOTAL_ELEMENTS,
} from '../../../../../../../reducers/getClusterStoreModal/totalElements';
import { useEditedPriceManagerStore } from '../../../../../../../store/priceManagerStore';
import { checkIfIsLoading } from '../../../../../../../utils';
import { handleMoveFocusToNextRowInput } from '../../../../utils';
import * as calcHandlers from '../../../../utils/utils';
import { restoreModalRow } from '../../../../utils/restoreModalRow';
import { CpiCell } from '../../../TableCells/CpiCell';
import { GET_ITEMS_CLUSTER_STORE_MODAL, UPDATE_PRODUCT_CLUSTER_PRICES } from '../../service';
import { ContentClusterStoreModal, GET_ITEMS_CLUSTER_STORE_MODAL_RESPONSE, ProductDataDTO, UpdateProductDataDTO } from '../../types';
import { GET_CLUSTER_STORE_DATAPOINTS_MODEL } from '../../utils';
import styles from './GetClusterStoreTable.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

const DATA_KEY = 'itemsClusterStore';

const MemoStoreProductClusterCell = memo(StoreProductClusterCell);
const MemoCpiCell = memo(CpiCell);
const MemoHoverNomeColuna = memo(HoverNomeColuna);

// O modal salva em lote pelo botão SALVAR ALTERAÇÕES: sair do campo não persiste nada
const handleBlurNoop = () => undefined;

const GetClusterStoreTable = ({ clusterId, onHide, productId }: { clusterId: string; onHide: () => void; productId: string }) => {
  const queryClient = useQueryClient();
  const editedDatapoints = useSelector(selectorGetEditedClusterStoreDatapoints);
  const dispatch = useDispatch();
  const pagination = useSelector(selectorGetClusterStoreModalPagination);
  const totalElements = useSelector(selectorGetClusterStoreModalTotalElements);
  const sort = useSelector(selectorGetClusterStoreModalSort);
  const datapoints = useSelector(selectorGetClusterStoreModalDatapoints);

  const { isPendingEditedPrice } = useEditedPriceManagerStore();

  const handleSortColumn = useCallback(
    (sortColumn: string, sortType: TableProps['sortType']) => {
      dispatch(
        SET_GET_CLUSTER_STORE_MODAL_SORT({
          type: sortColumn,
          orderBy: sortType,
        }),
      );
    },
    [dispatch],
  );

  const closeGetClusterStoreModal = () => {
    onHide();
    dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(false));
    dispatch(CLEAR_EDITED_CLUSTER_STORE_DATAPOINTS());
    dispatch(RESET_GET_CLUSTER_STORE_MODAL_DATAPOINTS());
    dispatch(RESET_GET_CLUSTER_STORE_MODAL_PAGINATION());
    dispatch(RESET_GET_CLUSTER_STORE_MODAL_SORT());
    dispatch(RESET_GET_CLUSTER_STORE_MODAL_TOTAL_ELEMENTS());
  };

  const GET_DATAPOINTS_MODEL = useMemo(() => {
    return GET_CLUSTER_STORE_DATAPOINTS_MODEL({
      sort,
      pagination,
      clusters: [clusterId],
      productIds: productId,
    });
  }, [sort, pagination, clusterId, productId]);

  const handleGetDatapoints = useCallback(async () => {
    try {
      const res = await GET_ITEMS_CLUSTER_STORE_MODAL(GET_DATAPOINTS_MODEL);
      return res;
    } catch {
      throw new Error('Erro ao buscar produtos');
    }
  }, [GET_DATAPOINTS_MODEL]);

  const handleSuccess = useCallback(
    (data: GET_ITEMS_CLUSTER_STORE_MODAL_RESPONSE) => {
      const updatedContent = data.content.map(item => {
        const editedItem = editedDatapoints[item.productsToBePricedId];
        if (editedItem) {
          return {
            ...item,
            ...editedItem,
          };
        }
        return item;
      });

      dispatch(SET_GET_CLUSTER_STORE_MODAL_DATAPOINTS(updatedContent));
      dispatch(SET_GET_CLUSTER_STORE_MODAL_TOTAL_ELEMENTS(data.totalElements));
    },
    [dispatch, editedDatapoints],
  );

  const handleChangePage = useCallback(
    (page: number) => {
      dispatch(SET_GET_CLUSTER_STORE_MODAL_ACTIVE_PAGE(page - 1));
    },
    [dispatch],
  );

  const handleChangeLength = useCallback(
    (size: number) => {
      const lastValidIndex = Math.ceil(totalElements / size);

      const isOnInvalidLastIndex = pagination.activePage + 1 > lastValidIndex;

      if (isOnInvalidLastIndex) handleChangePage(lastValidIndex);

      dispatch(SET_GET_CLUSTER_STORE_MODAL_DISPLAY_LENGTH(size));
    },
    [dispatch, totalElements, pagination.activePage, handleChangePage],
  );

  const memoPagination = useMemo(() => {
    return {
      total: totalElements,
      activePage: pagination.activePage + 1,
      displayLength: pagination.displayLength,
      lengthMenu: [
        { value: 25, label: '25' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
      ],
      onChangePage: handleChangePage,
      onChangeLength: handleChangeLength,
    };
  }, [totalElements, pagination, handleChangePage, handleChangeLength]);

  const { fetchStatus } = useQuery({
    queryKey: ['ipa/gerenciador/clusterStore', GET_DATAPOINTS_MODEL],
    queryFn: handleGetDatapoints,
    onSuccess: handleSuccess,
    retry: false,
    enabled: !isPendingEditedPrice,
  });

  const isLoading = useMemo(() => checkIfIsLoading(fetchStatus), [fetchStatus]);

  const handleRestoreRow = useCallback(
    (rowData: RowData, index: number) => {
      restoreModalRow({
        dispatch,
        rowData,
        index,
        editedDatapoints,
        updateDatapoint: UPDATE_GET_CLUSTER_STORE_MODAL_DATAPOINT,
        updateEditedDatapoint: UPDATE_EDITED_CLUSTER_STORE_DATAPOINT,
      });
    },
    [dispatch, editedDatapoints],
  );

  const handleUpdateCampo = useCallback(
    (name: GerenciadorPrecos.Inputs, value: number, rowData: RowData, index: number) => {
      let updatedData: Partial<RowData> = {
        priceStatus: 'EDITADO' as 'EDITED',
      };

      if (value > 0) {
        updatedData = { ...updatedData, novoCalculo: true };
        switch (name) {
          case 'novoPreco':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoPreco(value, rowData),
            };
            break;
          case 'novaCompetitividade':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoCompetitividade(value, rowData),
            };
            break;
          case 'novaMargem':
            updatedData = {
              ...updatedData,
              ...calcHandlers.handleCalculoMargem(value, rowData),
            };
            break;
          default:
            break;
        }
      } else {
        updatedData = {
          ...updatedData,
          price: value,
          novaMargem: 0,
          novaCompetitividade: 0,
        };
      }

      dispatch(
        UPDATE_EDITED_CLUSTER_STORE_DATAPOINT({
          key: rowData.productsToBePricedId,
          updatedData: {
            ...rowData,
            ...updatedData,
          },
        }),
      );

      dispatch(
        UPDATE_GET_CLUSTER_STORE_MODAL_DATAPOINT({
          index,
          updatedData,
        }),
      );
    },
    [dispatch],
  );

  const formattedData = useMemo((): UpdateProductDataDTO => {
    const productDataDTOList: ProductDataDTO[] = Object.keys(editedDatapoints).map(productsToBePricedId => {
      const editedItem = editedDatapoints[productsToBePricedId];
      return {
        productsToBePricedId,
        editedPrice: editedItem.price ?? 0,
      };
    });
    return {
      productDataDTOList,
    };
  }, [editedDatapoints]);

  const { mutate } = useMutation(UPDATE_PRODUCT_CLUSTER_PRICES, {
    onSuccess: () => {
      Alert.success('Alterações realizadas com sucesso!');
      queryClient.invalidateQueries(['ipa.gerenciador.datapoints']);
    },
    onError: () => {
      const { message } = new Exception('Erro ao atualizar preços!');
      AlertWithTimestamp({ message });
    },
  });

  const handleSaveChanges = () => {
    if (Object.keys(editedDatapoints).length === 0) {
      closeGetClusterStoreModal();
    } else {
      mutate(formattedData);
      dispatch(CLEAR_EDITED_CLUSTER_STORE_DATAPOINTS());
      dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(false));
      closeGetClusterStoreModal();
    }
  };

  useEffect(() => {
    const tableContainer = document.getElementById('cluster-store-table');
    if (tableContainer) {
      tableContainer.addEventListener('keydown', e => {
        handleMoveFocusToNextRowInput(e) as unknown as HTMLInputElement;
      });
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('keydown', e => {
          handleMoveFocusToNextRowInput(e) as unknown as HTMLInputElement;
        });
      }
    };
  }, []);

  return (
    <Layout.Section padded className={styles['layout-section']}>
      <Table
        id="cluster-store-table"
        dataKey={DATA_KEY}
        data={datapoints}
        className={classNames('ipa-table')}
        loading={isLoading || isPendingEditedPrice}
        sortColumn={sort.type}
        sortType={sort.orderBy as TableProps['sortType']}
        headerHeight={46}
        rowHeight={62}
        onSortColumn={handleSortColumn}
        height={500}
      >
        {/* @ts-ignore */}

        <Column sortable flexGrow={1}>
          <HeaderCell>Loja</HeaderCell>
          <Cell dataKey="productIds">{(rowData: ContentClusterStoreModal) => <MemoStoreProductClusterCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable minWidth={78}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>PMZ e</p>
              <p>custo</p>
            </div>
          </HeaderCell>
          <Cell dataKey="pmz">{(rowData: RowData) => <PMZCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable width={124}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>Preço</p>
              <p>vigente</p>
            </div>
          </HeaderCell>
          <Cell dataKey="priceRetail">{(rowData: RowData) => <PrecoVigenteCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column sortable width={127}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>Preço</p>
              <p>concorrente</p>
            </div>
          </HeaderCell>
          <Cell dataKey="competitorsPrice">{(rowData: RowData) => <CompetitorPriceCell rowData={rowData} />}</Cell>
        </Column>
        {/* @ts-ignore */}
        <Column width={132}>
          <HeaderCell>Preço sugerido</HeaderCell>
          <Cell className={styles['table__input-group']} dataKey="price">
            {(rowData: RowData, index: number) => (
              <PrecoSugeridoCell
                className={classNames(styles['table__input-group'], styles['gerenciador-page__table__input-group'])}
                dataKey="novoPreco"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={116}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>Margem</HeaderCell>
          <Cell className={styles['table__input-group']} dataKey="newMargin">
            {(rowData: RowData, index: number) => (
              <MargemCell
                dataKey="novaMargem"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleRestoreRow={handleRestoreRow}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>
        {/* @ts-ignore */}
        <Column width={116}>
          <HeaderCell>
            <MemoHoverNomeColuna placement="bottom" text="CPI" tooltip="Competitividade" />
          </HeaderCell>
          <Cell dataKey="cpi">
            {(rowData: RowData, index: number) => (
              <MemoCpiCell
                dataKey="novaCompetitividade"
                rowData={rowData}
                index={index}
                handleUpdateCampo={handleUpdateCampo}
                handleBlur={handleBlurNoop}
              />
            )}
          </Cell>
        </Column>
      </Table>

      <Pagination {...memoPagination} />

      <div className={styles.footer}>
        <ButtonPrimary onClick={closeGetClusterStoreModal} type="reset" theme="ghost">
          FECHAR
        </ButtonPrimary>
        <ButtonPrimary onClick={handleSaveChanges} type="submit">
          SALVAR ALTERAÇÕES
        </ButtonPrimary>
      </div>
    </Layout.Section>
  );
};

export default GetClusterStoreTable;
